import { useState, type ReactNode } from "react";
import { Badge, Card, Collapse, Group, NumberInput, Select, SimpleGrid, Skeleton, Stack, TagsInput, Text, TextInput, UnstyledButton } from "@mantine/core";
import { IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import { fmtNumber, type NutritionEstimate } from "@passio/api-client";
import { MacroChips } from "../theme/components";
import { buildPreferences, hasNutrition, type PreferencesForm } from "../lib/kitchen";

export const DIET_OPTIONS = ["vegetarian", "vegan", "pescatarian", "keto", "low-carb", "gluten-free", "dairy-free", "halal", "kosher", "paleo", "low-FODMAP", "Mediterranean"];
export const ALLERGY_OPTIONS = ["milk", "eggs", "fish", "shellfish", "tree nuts", "peanuts", "wheat", "soy", "sesame"];
export const CONDITION_OPTIONS = [
  "type 2 diabetes",
  "prediabetes",
  "high blood pressure",
  "high cholesterol",
  "GERD / acid reflux",
  "IBS",
  "celiac disease",
  "gout",
  "chronic kidney disease",
  "pregnancy",
  "taking a GLP-1 medication",
];
export const GOAL_OPTIONS = ["lose weight", "build muscle", "high protein", "more fiber", "lower sodium", "less sugar", "heart health", "steady energy"];

type Field = keyof PreferencesForm;

/** Collapsible preferences: likes, dislikes, diet, allergies and whichever extras a tool uses. */
export function PreferencesFields({
  value,
  onChange,
  fields = ["likes", "dislikes", "diet", "allergies"],
  defaultOpen = false,
}: {
  value: PreferencesForm;
  onChange: (v: PreferencesForm) => void;
  fields?: Field[];
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const set = (patch: Partial<PreferencesForm>) => onChange({ ...value, ...patch });
  const count = Object.keys(buildPreferences(value) ?? {}).length;
  const has = (f: Field) => fields.includes(f);
  const tags = (field: "likes" | "dislikes" | "equipment") => splitTags(value[field]);
  return (
    <Stack gap={6}>
      <UnstyledButton onClick={() => setOpen((o) => !o)}>
        <Group gap={6}>
          {open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
          <Text fz="sm" fw={600}>
            Preferences
          </Text>
          {count > 0 && (
            <Badge size="xs" color="lime" c="ink.8">
              {count} set
            </Badge>
          )}
        </Group>
      </UnstyledButton>
      <Collapse in={open}>
        <Stack gap="xs" pl={20}>
          {has("likes") && <TagsInput label="Likes" placeholder="spicy, garlic, crispy" value={tags("likes")} onChange={(v) => set({ likes: v.join(", ") })} />}
          {has("dislikes") && <TagsInput label="Dislikes" placeholder="mushrooms, cilantro" value={tags("dislikes")} onChange={(v) => set({ dislikes: v.join(", ") })} />}
          {has("diet") && <TagsInput label="Diet" data={DIET_OPTIONS} value={value.diet ?? []} onChange={(v) => set({ diet: v })} placeholder="Pick or type" />}
          {has("allergies") && <TagsInput label="Allergies" data={ALLERGY_OPTIONS} value={value.allergies ?? []} onChange={(v) => set({ allergies: v })} placeholder="Never included" />}
          {has("conditions") && <TagsInput label="Health conditions" data={CONDITION_OPTIONS} value={value.conditions ?? []} onChange={(v) => set({ conditions: v })} placeholder="Pick or type" />}
          {has("goals") && <TagsInput label="Goals" data={GOAL_OPTIONS} value={value.goals ?? []} onChange={(v) => set({ goals: v })} placeholder="Pick or type" />}
          {(has("servings") || has("maxMinutes")) && (
            <SimpleGrid cols={2} spacing="xs">
              {has("servings") && <NumberInput label="Servings" min={1} max={12} value={value.servings ?? ""} onChange={(v) => set({ servings: v })} />}
              {has("maxMinutes") && <NumberInput label="Max minutes" min={5} max={240} step={5} value={value.maxMinutes ?? ""} onChange={(v) => set({ maxMinutes: v })} />}
            </SimpleGrid>
          )}
          {(has("cuisine") || has("skill")) && (
            <SimpleGrid cols={2} spacing="xs">
              {has("cuisine") && <TextInput label="Cuisine" placeholder="Any" value={value.cuisine ?? ""} onChange={(e) => set({ cuisine: e.currentTarget.value })} />}
              {has("skill") && <Select label="Skill" placeholder="Any" data={["beginner", "intermediate", "confident"]} clearable value={value.skill || null} onChange={(v) => set({ skill: v ?? "" })} />}
            </SimpleGrid>
          )}
          {has("equipment") && <TagsInput label="Equipment I have" placeholder="air fryer, sheet pan" value={tags("equipment")} onChange={(v) => set({ equipment: v.join(", ") })} />}
          {has("timeOfDay") && (
            <Select label="Time of day" placeholder="Now" data={["morning", "midday", "afternoon", "evening", "late night"]} clearable value={value.timeOfDay || null} onChange={(v) => set({ timeOfDay: v ?? "" })} />
          )}
          {has("targets") && (
            <SimpleGrid cols={3} spacing="xs">
              {(
                [
                  ["calories", "Calories"],
                  ["proteinGrams", "Protein g"],
                  ["carbsGrams", "Carbs g"],
                  ["fatGrams", "Fat g"],
                  ["fiberGrams", "Fiber g"],
                  ["sodiumMg", "Sodium mg"],
                ] as const
              ).map(([k, label]) => (
                <NumberInput key={k} label={label} min={0} hideControls value={value.targets?.[k] ?? ""} onChange={(v) => set({ targets: { ...value.targets, [k]: v === "" ? undefined : Number(v) } })} />
              ))}
            </SimpleGrid>
          )}
        </Stack>
      </Collapse>
    </Stack>
  );
}

const splitTags = (s: string | undefined) => (s ? s.split(",").map((x) => x.trim()).filter(Boolean) : []);

/** Calories and macros from an estimate, plus fiber, sugar and sodium when present. */
export function NutritionChips({ n, label, size = "sm" }: { n: NutritionEstimate | null | undefined; label?: ReactNode; size?: "xs" | "sm" | "md" }) {
  if (!hasNutrition(n)) return null;
  const extras = [
    n.fiberGrams ? `fiber ${fmtNumber(n.fiberGrams, 1)} g` : "",
    n.sugarGrams ? `sugar ${fmtNumber(n.sugarGrams, 1)} g` : "",
    n.sodiumMg ? `sodium ${fmtNumber(n.sodiumMg)} mg` : "",
  ].filter(Boolean);
  return (
    <Group gap={8} wrap="wrap">
      <MacroChips calories={n.calories ?? undefined} protein={n.proteinGrams ?? undefined} carbs={n.carbsGrams ?? undefined} fat={n.fatGrams ?? undefined} size={size} />
      {extras.length > 0 && (
        <Text fz="xs" c="dimmed">
          {extras.join(" · ")}
        </Text>
      )}
      {label && (
        <Text fz="xs" c="dimmed">
          {label}
        </Text>
      )}
    </Group>
  );
}

export function KitchenPending({ title }: { title: string }) {
  return (
    <Card>
      <Text fw={650} mb="sm">
        {title}
      </Text>
      <Stack gap="xs">
        <Skeleton h={28} w="60%" radius="md" />
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} h={44} radius="md" />
        ))}
      </Stack>
    </Card>
  );
}

export function ExampleList({ examples, onPick }: { examples: string[]; onPick: (ex: string) => void }) {
  return (
    <Stack gap={6}>
      <Text fz="xs" c="dimmed">
        Examples
      </Text>
      {examples.map((ex) => (
        <Text key={ex} fz="sm" c="violet.7" style={{ cursor: "pointer" }} onClick={() => onPick(ex)}>
          “{ex}”
        </Text>
      ))}
    </Stack>
  );
}

/** A short list under a small uppercase heading; renders nothing when empty. */
export function BulletSection({ title, items, color }: { title: string; items: string[] | undefined; color?: string }) {
  if (!items?.length) return null;
  return (
    <Stack gap={4}>
      <Text fz="xs" fw={700} c="dimmed" tt="uppercase" style={{ letterSpacing: "0.05em" }}>
        {title}
      </Text>
      {items.map((t, i) => (
        <Group key={i} gap={8} wrap="nowrap" align="flex-start">
          <Text fz="sm" c={color ?? "violet.6"} lh={1.45}>
            •
          </Text>
          <Text fz="sm">{t}</Text>
        </Group>
      ))}
    </Stack>
  );
}
